import { Injectable } from '@angular/core';
import { Animation, Gesture, GestureController, GestureDetail } from '@ionic/angular';
import { getTimeGivenProgression } from '@core/services/modal/cubic-beizer';

@Injectable({
    providedIn: 'root'
})
export class SwipeToCloseGestureService {
    private animation: Animation;
    private height: number;
    private gesture: Gesture;
    private isOpen = false;
    private modalElement: HTMLIonModalElement;
    private preventStarts: { [key: string]: boolean } = {};
    private onDismiss: () => void;
    private overflowValue: any;
    private scrollElement: any;

    constructor() {
    }

    private static clamp(min: number, n: number, max: number): number {
        return Math.max(min, Math.min(n, max));
    }

    private static computeDuration(remaining: number, velocity: number): number {
        return SwipeToCloseGestureService.clamp(400, remaining / Math.abs(velocity * 1.1), 500);
    }

    private canStart(detail: GestureDetail): boolean {
        const target = detail.event.target as HTMLElement | null;

        if (target === null || !(target as any).closest) {
            return true;
        }

        const content = target.closest('ion-content');
        if (content === null) {
            return true;
        } else {
            const id = content.id !== '' ? content.id : 'ionContent';
            return !this.preventStarts[id];
        }
    }

    private onStart(): void {
        if (this.scrollElement !== undefined) {
            this.overflowValue = 'auto';
            this.scrollElement.style.overflow = this.overflowValue;
        }

        this.animation.progressStart(true, (this.isOpen) ? 1 : 0);
    }

    private onMove(detail: GestureDetail): void {
        if (this.scrollElement !== undefined && this.overflowValue !== 'hidden' && detail.deltaY >= 0) {
            this.overflowValue = 'hidden';
            this.scrollElement.style.overflow = this.overflowValue;
        }

        const step = SwipeToCloseGestureService.clamp(0.0001, detail.deltaY / this.height, 0.9999);
        this.animation.progressStep(step);
    }

    private onEnd(detail: GestureDetail): void {
        if (this.scrollElement !== undefined) {
            this.overflowValue = 'auto';
            this.scrollElement.style.overflow = this.overflowValue;
        }

        const velocity = detail.velocityY;
        const step = SwipeToCloseGestureService.clamp(0.0001, detail.deltaY / this.height, 0.9999);
        const threshold = (detail.deltaY + velocity * 1000) / this.height;
        const shouldComplete = threshold >= 0.5;

        let newStepValue = (shouldComplete) ? -0.001 : 0.001;

        if (!shouldComplete) {
            this.animation.easing('cubic-bezier(1, 0, 0.68, 0.28)');
            newStepValue += getTimeGivenProgression([0, 0], [1, 0], [0.68, 0.28], [1, 1], step)[0];
        } else {
            this.animation.easing('cubic-bezier(0.32, 0.72, 0, 1)');
            newStepValue += getTimeGivenProgression([0, 0], [0.32, 0.72], [0, 1], [1, 1], step)[0];
        }

        const duration = (shouldComplete) ?
            SwipeToCloseGestureService.computeDuration(step * this.height, velocity) :
            SwipeToCloseGestureService.computeDuration((1 - step) * this.height, velocity);

        this.isOpen = shouldComplete;
        this.gesture.enable(false);
        this.animation.onFinish(() => {
            if (!shouldComplete) {
                this.gesture.enable(true);
            }
        })
            .progressEnd((shouldComplete) ? 1 : 0, newStepValue, duration);

        if (shouldComplete) {
            this.onDismiss();
        }
    }

    public checkForIonContents(): void {
        const ionContents: any = this.modalElement.getElementsByTagName('ion-content');
        this.preventStarts = {};

        for (let i = 0; i < ionContents.length; i++) {
            const ionContent: HTMLIonContentElement = ionContents.item(i);

            if (ionContent !== undefined) {
                const id = ionContent.id !== '' ? ionContent.id : 'ionContent';
                this.preventStarts[id] = false;

                ionContent.scrollEvents = true;
                ionContent.getScrollElement().then((scrollElem: any) => {
                    this.scrollElement = scrollElem;
                    this.scrollElement.addEventListener('scroll', (scrollEvent: any) => {
                        this.preventStarts[id] = scrollEvent.target.scrollTop > 0;
                    });
                });
            }
        }

    }

    public createGesture(
        gestureController: GestureController,
        el: HTMLIonModalElement,
        animation: Animation,
        onDismiss: () => void): Gesture {
        this.animation = animation;
        this.height = el.offsetHeight;
        this.modalElement = el;
        this.onDismiss = onDismiss;

        this.checkForIonContents();

        this.gesture = gestureController.create({
            el,
            gestureName: 'modalSwipeToClose',
            gesturePriority: 40,
            direction: 'y',
            threshold: 10,
            canStart: detail => this.canStart(detail),
            onStart: () => this.onStart(),
            onMove: detail => this.onMove(detail),
            onEnd: detail => this.onEnd(detail)
        }, true);

        return this.gesture;
    }
}
